import React, { useEffect, useRef, useState } from 'react';

import type { EnrichedWithInsight } from './utils.js';
import type { ByfunnPromoArticle } from '../../../../../types/frapi-types.js';
import type { SiteVersion } from '../../../../../../core/rendering/types.js';

import { useEmbedHtml } from '../../../../hooks/useEmbedHtml.js';
import {
  enrichWithInsight,
  mapSiteversionToDeviceType,
  structureResponse,
} from './utils.js';
import { usePulseContext } from '../../../../../../core/react/pulse/PulseProvider.js';
import {
  trackSalesposterView,
  trackStaticPageView,
  withByfunnPulse,
} from '../../../../../pulse/index.js';

export type SalesposterProps = {
  siteVersion: SiteVersion;
  id: string;
  referrer?: string;
  article?: ByfunnPromoArticle;
  refDisplayName?: string;
};
const isEmpty = (obj: Record<string, unknown>) =>
  Object.keys(obj).length === 0 && obj.constructor === Object;

const getRefId = (referrer: string): string => {
  switch (referrer) {
    case 'vinkapfront':
      return '/vinkapfront';
    case 'vinklandingpage':
      return '/vinklandingpage';
    default:
      return document.referrer.includes('facebook') ? 'vink_facebook' : '';
  }
};

const Salesposter: React.FC<SalesposterProps> = ({
  siteVersion,
  id,
  referrer,
  article,
}) => {
  const [data, setData] = useState<EnrichedWithInsight>({});

  const ref = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    (async () => {
      try {
        const refId = referrer ? getRefId(referrer) : undefined;

        const res = await fetch(`/api/cm/vink/subscription${refId}`);

        const isApFront = refId === '/vinkapfront';

        const json = await res.json();
        const structured = structureResponse(json);

        const urlSearchParams = new URLSearchParams(window.location.search);
        const query = Object.fromEntries(urlSearchParams.entries());

        const enriched = enrichWithInsight(structured, {
          deviceType: mapSiteversionToDeviceType(siteVersion),
          contentType: article ? 'article' : 'page',
          contentProvider: isApFront ? 'ap' : 'vink',
          newsroom: 'byfunn',
          statisticsId: id,
          canonicalUrl: article
            ? `https://vink.aftenposten.no/artikkel/${id}`
            : 'https://vink.aftenposten.no/bli-abonnent',
          query,
        });
        setData(enriched);
      } catch (e) {
        setData({});
      }
    })();
  }, [id, siteVersion, referrer, article]);

  const { trackPageView } = usePulseContext();

  useEffect(() => {
    if (article && !isEmpty(data) && data.insightData) {
      trackSalesposterView(trackPageView, article, data.insightData);
    } else if (!article && !isEmpty(data)) {
      trackStaticPageView(
        trackPageView,
        'Page',
        'bli-abonnent',
        data.insightData,
      );
    }
  }, [trackPageView, article, data]);

  const height = useEmbedHtml(ref, data);

  return (
    <div className="salesposter-container">
      {!isEmpty(data) ? (
        <iframe
          ref={ref}
          title={`salesposter-${id}`}
          height={Math.min(760, height)}
          width="100%"
          style={{ border: 'none', overflowY: 'hidden' }}
          seamless={true}
          scrolling="no"
        />
      ) : (
        <div>
          Vil du lese mer? <a href="/bli-abonnent">Bli abonnent</a>
        </div>
      )}
    </div>
  );
};

const SalesposterWithPulse = withByfunnPulse(Salesposter);

export { SalesposterWithPulse as Salesposter };
